import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  ExecuteVideoOperationRequest,
  ExtendRequest,
  ImageToVideoRequest,
  InterpolateRequest,
  RestyleRequest,
  TextToVideoRequest,
  VideoLabOperationRequest,
  VideoOperationResult,
  VideoOperationStatus,
  VideoOperationType,
} from '../domain';

const BASE = '/api/v3/video/operations';

const TERMINAL: readonly VideoOperationStatus[] = ['Completed', 'Failed', 'Cancelled'];

/** PascalCase op type → kebab-case typed route segment. */
const ROUTE_BY_KIND: Record<VideoOperationType, string> = {
  TextToVideo: 'text-to-video',
  ImageToVideo: 'image-to-video',
  Interpolate: 'interpolate',
  Extend: 'extend',
  Restyle: 'restyle',
};

/**
 * Animation Lab video operations. Mirrors TextOperationService — one method per BE
 * endpoint, each unwrapping the ApiResponse envelope via callV3. Unlike the image/
 * text labs, these jobs are genuinely async: `getStatus` re-polls the provider and
 * `cancel` stops a running render (cost-safety).
 */
export const VideoOperationService = {
  execute: (request: ExecuteVideoOperationRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/execute`, request),
    ),

  textToVideo: (request: TextToVideoRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/text-to-video`, request),
    ),

  imageToVideo: (request: ImageToVideoRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/image-to-video`, request),
    ),

  interpolate: (request: InterpolateRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/interpolate`, request),
    ),

  extend: (request: ExtendRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/extend`, request),
    ),

  restyle: (request: RestyleRequest) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/restyle`, request),
    ),

  /** Dispatch to the typed route for `kind`. The submit hook (A-F4) calls this. */
  submit(kind: VideoOperationType, request: VideoLabOperationRequest) {
    const path = ROUTE_BY_KIND[kind];
    return callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/${path}`, request),
    );
  },

  /** Real poll — the BE re-queries the provider and persists the asset on completion. */
  getStatus: (operationId: string) =>
    callV3<VideoOperationResult>(
      api.get<ApiResponse<VideoOperationResult>>(`${BASE}/jobs/${operationId}`),
    ),

  /** Stop a running render (cost-safety; used by cancel-on-unmount). */
  cancel: (operationId: string) =>
    callV3<VideoOperationResult>(
      api.post<ApiResponse<VideoOperationResult>>(`${BASE}/jobs/${operationId}/cancel`, {}),
    ),

  async pollStatus(
    operationId: string,
    onProgress?: (result: VideoOperationResult) => void,
    maxAttempts = 150,
    intervalMs = 2000,
  ): Promise<VideoOperationResult> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await this.getStatus(operationId);
      onProgress?.(result);
      if (TERMINAL.includes(result.status)) {
        return result;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error(
      `Video operation '${operationId}' polling timed out after ${maxAttempts * intervalMs}ms`,
    );
  },
};
